import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import CollapseCard from './ui/CollapseCard';
import FeePolicyEditor from './ui/FeePolicyEditor';
import { MODE_LABELS } from '../ReservationConfigForm';
import { err, clsErr, hasErrPref } from '../shared/validation';
import Required from '../utils/Required';
import clsx from 'clsx';

export default function FeePoliciesForm({
  value = [],
  modeData = {},
  onChange,
  errors = {},
}) {
  /* ---------- CRUD helpers -------------------------------- */
  const add = () =>
    onChange([
      ...value,
      {
        id: uuid(),
        label: '',
        scope: 'all',
        applies_to: {},
        fee: {
          /* new structure – sensible defaults */
          cancellation_fee_rules: [],
          no_show_fee: 0,
          no_show_fee_type: 'percent',
          no_show_timeout_value: 15,
          no_show_timeout_unit: 'minutes',
          prepayment: 0,
          prepayment_type: 'percent',
          charge_before_value: 0,
          charge_before_unit: 'days',
          full_prepayment_required: false,
        },
      },
    ]);

  const rm = (id) => onChange(value.filter((f) => f.id !== id));
  const patch = (id, payload) =>
    onChange(value.map((f) => (f.id === id ? payload : f)));

  /* ---------- items per mode for the scope-picker ---------- */
  const modeItems = useMemo(
    () => ({
      personal_services: (modeData.personal_services?.services ?? []).map((o) => ({
        id: o.id,
        name: o.name,
      })),
      table_inventory: (modeData.table_inventory?.services ?? []).map((service) => ({
        id: service.id,
        name: service.name || 'Dining service',
      })),
      class_based: (modeData.class_based?.classes ?? []).map((o) => ({
        id: o.id,
        name: o.title,
      })),
      accommodation: (modeData.accommodation?.room_types ?? []).map((r, idx) => ({
        id: r.id ?? `room_${idx}`,
        name: r.name,
      })),
    }),
    [modeData],
  );
const hasAllScope = value.some((fp) => fp.scope === 'all');
  const maxPolicies = 20;

  /* ---------- JSX ----------------------------------------- */
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={add}
          className={clsx(
            'text-sm',
            hasAllScope || (value?.length||0) >= maxPolicies
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-blue-600 hover:underline',
          )}
          disabled={hasAllScope || (value?.length||0) >= maxPolicies}
        >
          + Add fee policy
        </button>
        <span className="text-xs text-gray-500">{(value?.length||0)}/{maxPolicies}</span>
        {err(errors, 'fee_policies') && (
          <span className="text-xs text-red-600">{err(errors, 'fee_policies')}</span>
        )}
      </div>

      {value.map((fp, idx) => {
        const rootKey = `fee_policies${idx}`;
        const rowErr = hasErrPref(errors, rootKey);

        const customScopeEmpty =
          fp.scope === 'custom' &&
          Object.values(fp.applies_to || {}).every((arr) => !arr?.length);

        return (
          <CollapseCard
            key={fp.id}
            title={
              <>
                {fp.label || `Policy ${idx + 1}`}
                {rowErr && <Required />}
              </>
            }
            error={rowErr}
            right={
              <button
                onClick={() => rm(fp.id)}
                className="text-red-500 text-xs hover:underline"
              >
                Remove
              </button>
            }
          >
            {/* ── Label & Scope ─────────────────────────────── */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start text-sm">
              <label className="w-full">
                <span className="block mb-1">
                  Label <Required />
                </span>
                <input
                  className={clsx(
                    'w-full rounded border-gray-300 px-2 py-1',
                    clsErr(errors, `${rootKey}.label`),
                  )}
                  value={fp.label}
                  onChange={(e) =>
                    patch(fp.id, { ...fp, label: e.target.value })
                  }
                />
                {err(errors, `${rootKey}.label`) && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {err(errors, `${rootKey}.label`)}
                  </p>
                )}
              </label>

              <label className="w-full">
                <span className="block mb-1">
                  Scope <Required />
                </span>
                <select
                  value={fp.scope}
                  onChange={(e) =>
                    patch(fp.id, { ...fp, scope: e.target.value })
                  }
                  className={clsx(
                    'w-full rounded border-gray-300 px-2 py-1',
                    clsErr(errors, `${rootKey}.scope`),
                  )}
                >
                  <option value="all">All offerings</option>
                  <option value="custom">Custom selection</option>
                </select>
                {(err(errors, `${rootKey}.scope`) || customScopeEmpty) && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {err(errors, `${rootKey}.scope`) ||
                      'Select at least one offering'}
                  </p>
                )}
              </label>
            </div>

            {/* ── Fee editor ─────────────────────────────────── */}
            <FeePolicyEditor
              rowIndex={idx}
              value={fp.fee}
              errors={errors}
              onChange={(feePatch) => {
                /* auto-fill logic for the 100 % checkbox */
                if (feePatch.full_prepayment_required) {
                  feePatch = {
                    ...feePatch,
                    prepayment: 100,
                    prepayment_type: 'percent',
                  };
                } else if (
                  'full_prepayment_required' in feePatch &&
                  (feePatch.prepayment === undefined ||
                    feePatch.prepayment === 100)
                ) {
                  feePatch = {
                    ...feePatch,
                    prepayment: 0,
                    prepayment_type: 'percent',
                  };
                }
                patch(fp.id, { ...fp, fee: feePatch });
              }}
            />

            {/* ── Custom selection ───────────────────────────── */}
            {fp.scope === 'custom' && (
              <div className="mt-4 space-y-6">
                {Object.entries(modeItems).map(([m, items]) =>
                  items.length ? (
                    <div key={m} className="space-y-1">
                      <h4 className="font-semibold text-sm mb-1">
                        {MODE_LABELS[m]}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {items.map(({ id, name }, i) => {
                          const sel = fp.applies_to?.[m]?.includes(id);
                          const alreadyUsed = value.some((other) => 
                            other.id !== fp.id &&
                            other.scope === 'custom' &&
                            (other.applies_to?.[m] ?? []).includes(id)
                          );
                          return (
                            <label
                              key={id}
                              className="text-xs flex items-center gap-1"
                            >
                              <input
                                type="checkbox"
                                checked={sel}
                                disabled={alreadyUsed}
                                onChange={() => {
                                  const list = fp.applies_to?.[m] ?? [];
                                  const next = sel
                                    ? list.filter((x) => x !== id)
                                    : [...list, id];
                                  patch(fp.id, {
                                    ...fp,
                                    applies_to: {
                                      ...fp.applies_to,
                                      [m]: next,
                                    },
                                  });
                                }}
                              />
                              {name || `Item ${i + 1}`}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null,
                )}
              </div>
            )}
          </CollapseCard>
        );
      })}
    </div>
  );
}
